import axios from 'axios';
import create from 'zustand';
import { LoginFormData, User } from 'ts/types';
import { ErrorResponse } from 'ts/types/ApiResponse';
import { http, ls } from 'utils';
import toast from 'react-hot-toast';
import CONFIG from 'config/constants';
import {
  clearAuthTokens,
  ensureAccessToken,
  refreshAccessToken,
  setOnAuthFailure,
  shouldRefreshAccessToken,
  storeAuthTokens,
} from 'utils/tokenRefresh';

type UserStore = {
  isLoggedIn: boolean;
  isLoading: boolean;
  isError: boolean;
  error: ErrorResponse | null;
  user: User | null;
  signIn: (formData: LoginFormData) => Promise<boolean>;
  signOut: () => void;
  getUser: () => void;
  resetInactivityTimer: () => void;
};

interface LoginResponse {
  token: string;
  accessToken: string;
  accessTokenExpiration: string;
  refreshToken: string;
  refreshTokenExpiration: string;
  user: User;
}

const useAuthStore = create<UserStore>((set, get) => {
  let inactivityTimer: NodeJS.Timeout;

  const updateLastActivityTime = () => {
    localStorage.setItem('lastActivityTime', Date.now().toString());
  };

  const signOut = (): void => {
    clearAuthTokens();
    ls.remove('selectedTrader');
    localStorage.removeItem('lastActivityTime');
    set({ isLoggedIn: false, user: null });
    if (inactivityTimer) {
      clearTimeout(inactivityTimer);
    }
  };

  setOnAuthFailure(() => {
    const wasLoggedIn = get().isLoggedIn || !!ls.get(CONFIG.LSKEY_TOKEN) || !!ls.get(CONFIG.LSKEY_REFRESH_TOKEN);
    signOut();
    if (wasLoggedIn) {
      toast.error('Session expired - log in to continue.');
    }
  });

  const startInactivityTimer = () => {
    updateLastActivityTime();

    if (inactivityTimer) { clearTimeout(inactivityTimer); }

    if (shouldRefreshAccessToken()) {
      refreshAccessToken();
    }

    inactivityTimer = setTimeout(() => {
      signOut();
      toast.error("Session timed out - log in to continue.");
    }, CONFIG.INACTIVITY_TIMEOUT_MS);
  };

  const checkInactivityOnWake = () => {
    const lastActivityTime = localStorage.getItem('lastActivityTime');
    if (!lastActivityTime) return;

    const elapsed = Date.now() - parseInt(lastActivityTime, 10);
    if (elapsed >= CONFIG.INACTIVITY_TIMEOUT_MS) {
      signOut();
      toast.error("Session timed out - log in to continue.");
    } else {
      startInactivityTimer();
    }
  };

  const resetInactivityTimer = () => {
    startInactivityTimer();
  };

  const hasStoredSession =
    typeof window !== 'undefined' &&
    (!!ls.get(CONFIG.LSKEY_TOKEN) || !!ls.get(CONFIG.LSKEY_REFRESH_TOKEN));

  if (hasStoredSession) {
    setTimeout(() => {
      ensureAccessToken().then((token) => {
        if (!token) return;
        checkInactivityOnWake();
      });
    }, 0);
  }

  return {
    isLoggedIn: hasStoredSession,
    isLoading: hasStoredSession,
    isError: false,
    error: null,
    user: null,

    signIn: async (formData): Promise<boolean> => {
      set({ isLoading: true });
      try {
        const { data } = await http.post<LoginResponse>(`${process.env.REACT_APP_API_HOST}/login`, formData);
        const {
          accessToken,
          accessTokenExpiration,
          refreshToken,
          token,
          user,
        } = data;

        storeAuthTokens(
          accessToken,
          refreshToken || token,
          accessTokenExpiration
        );
        set({
          isLoggedIn: true,
          isLoading: false,
          isError: false,
          error: null,
          user,
        });

        startInactivityTimer();
        return true;
      } catch (error) {
        set({ isLoading: false, isError: true, error: error as ErrorResponse });
        return false;
      }
    },

    signOut,

    getUser: async (): Promise<void> => {
      try {
        const token = await ensureAccessToken();
        if (!token) {
          set({ isLoading: false, isLoggedIn: false, user: null });
          return;
        }

        const { data } = await http.get<User>(`${process.env.REACT_APP_API_HOST}/profile`);
        set({ isLoggedIn: true, isLoading: false, isError: false, user: data });
        startInactivityTimer();
      } catch (error) {
        if (axios.isCancel(error)) {
          return;
        }

        const currentUser = get().user;
        if (currentUser) {
          set({ isLoading: false, isError: false, error: null });
          return;
        }

        set({ isLoading: false, isError: true, error: error as ErrorResponse });
      }
    },

    resetInactivityTimer,
  };
});

export default useAuthStore;
